import React, { Dispatch, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Col, Segmented, Select } from "antd";
import type { SegmentedValue } from "antd/es/segmented";
import type { SelectProps } from "antd";
import { useSeriesHooks } from "../hooks/useSeriesHooks";
import { CustomLabel } from "../StyledComponent";
import useScreenSizeHook from "../../utils/useScreenSizeHook";

type Filter = {
	status: "Active" | "All";
	content_provider: string;
};

type FiltersProps = {
	setFilteredTable: Dispatch<React.SetStateAction<any[]>>;
};

type SeriesItem = {
	status: string;
	content_provider: string;
	[key: string]: any;
};

const Filters: React.FC<FiltersProps> = ({ setFilteredTable }) => {
	const { seriesList, setseriesList, getSeriesData, setIsLoading } = useSeriesHooks();
	const { width } = useScreenSizeHook();

	const segmentedOptions = [ { label: "Active", value: "Active" }, { label: "All", value: "All" } ]

	const [filterOptions, setFilterOptions] = useState<Filter>({
		status: "Active",
		content_provider: "All",
	});

	const filterSeriesData = useCallback(
		(data: SeriesItem[], filter: Filter) => {
			const { status, content_provider } = filter;
			setIsLoading(true);

			const filtered = data.filter(item => {
				const matchesStatus = status === "All" || item.status?.toLowerCase() === status?.toLowerCase();
				const matchesProvider =
					content_provider === "All" || item.content_provider === content_provider;
				return matchesStatus && matchesProvider;
			});

			setFilteredTable(filtered);
			setIsLoading(false);
		},
		[setFilteredTable, setIsLoading]
	);

	const handleStatusChange = (value: SegmentedValue) => {
		setFilterOptions(prev => ({
			...prev,
			status: value as Filter["status"],
		}));
	};

	const handleSelect = (value: string) => {
		setFilterOptions(prev => ({
			...prev,
			content_provider: value,
		}));
	};

	const contentProviderOptions: SelectProps["options"] = useMemo(() => {
		const unique = new Set<string>();

		for (const item of seriesList) {
			if (item.content_provider) {
				unique.add(item.content_provider);
			}
		}

		const options = Array.from(unique).map(provider => ({
			value: provider,
			label: provider,
		}));

		return [{ value: "All", label: "All" }, ...options];
	}, [seriesList]);

	useEffect(() => {
		filterSeriesData(seriesList, filterOptions);
	}, [seriesList, filterOptions, filterSeriesData]);

	useEffect(() => {
		getSeriesData(setseriesList, setIsLoading, { column: "updated_at", order: "desc" });
	}, [getSeriesData, setseriesList, setIsLoading]);

	return (
		<Fragment>
			<Col>
				<CustomLabel width={width}>Status:</CustomLabel>
				<Segmented
					value={filterOptions.status}
					onChange={handleStatusChange}
					options={segmentedOptions}
				/>
			</Col>
			<Col>
				<CustomLabel width={width}>Provider:</CustomLabel>
				<Select
					showSearch
					value={filterOptions.content_provider}
					style={{ width: 200 }}
					placeholder="Select content provider"
					onSelect={handleSelect}
					filterSort={(a, b) => (a?.label ?? "").toString().localeCompare((b?.label ?? "").toString())}
					options={contentProviderOptions}
				/>
			</Col>
		</Fragment>
	);
};

export default Filters;
